<template>
  <div class="role-assignment">
    <section-title title="Información de Rol" has-line />
    <validation-provider
      ref="roleSelection"
      :mode="providerValidationMode"
      :rules="'rolesRequired:1'"
      v-slot="{ errors: roleErrors }"
      tag="div"
      class="mx-3 mb-3"
    >
      <chips-selection
        selected-items-title="Sistema(s) y Role(s) Seleccionado(s):"
        v-model="userModel.roles"
        @addingItem="addingRole"
        @removingItem="removingRole"
        :locked-items="getLockedItems"
        :errors="roleErrors"
        :chips-config="chipConfig"
        :disabled="readonly"
      >
        <template #selectionInputs>
          <validation-observer ref="roleForm" disabled>
            <div class="form-group">
              <validation-provider rules="required" v-slot="{ errors }">
                <custom-drop-down
                  v-model="model.app"
                  label="Sistema:"
                  :options="availableApps"
                  fieldname="name"
                  fieldkey="id"
                  placeholder="Seleccione"
                  :error="errors[0]"
                  custom-error-msg="El sistema es requerido."
                  @input="onAppSelected"
                />
              </validation-provider>
            </div>
            <div class="form-group">
              <validation-provider rules="required" v-slot="{ errors }">
                <custom-drop-down
                  label="Rol:"
                  v-model="model.role"
                  :options="options.roles"
                  fieldname="roleName"
                  fieldkey="roleId"
                  placeholder="Seleccione"
                  :error="errors[0]"
                  custom-error-msg="El rol es requerido."
                />
              </validation-provider>
            </div>
          </validation-observer>
        </template>
      </chips-selection>
    </validation-provider>

    <div
      class="mt-4"
      v-if="
        (canSetProfileInfo && !readonly) ||
          (canSetProfileInfo && hasProfileInformation)
      "
    >
      <section-title title="Información de Perfil por Aplicación" has-line />

      <position-assignment
        v-if="canSetPosition"
        ref="positionAssigment"
        :profile="userPosition"
        :readonly="readonly"
        :user-profile="userModel"
        :data="data"
        @accountdata="getGeneralAccountInfo"
      />

      <custom-tab class="profile-tabs">
        <custom-tab-item
          v-if="roleByApp(applicationIdsEnum.portalPadres)"
          title="Portal de Padres"
          :lazy="false"
        >
          <parent-profile
            ref="parentProfile"
            v-model="userModel"
            @student-selected="setStudentAddressIfExist"
            :readonly="readonly"
          />
        </custom-tab-item>
        <custom-tab-item
          v-if="roleByApp(applicationIdsEnum.Sipaee)"
          title="Portal de SIPAEE"
          :lazy="false"
        >
          <sipaee-profile
            ref="sipaeeProfile"
            v-model="userModel"
            @student-selected="setStudentAddressIfExist"
            :readonly="readonly"
          />
        </custom-tab-item>
        <custom-tab-item
          v-if="canAddLocation(applicationIdsEnum.mipe)"
          title="Portal Académico"
          :lazy="false"
        >
          <position-assignment
            class="mt-2"
            v-if="canSetPosition && readonly"
            ref="positionAssigment"
            :profile="userPosition"
            :readonly="readonly"
            :user-profile="userModel"
          />
          <mipe-profile
            ref="mipeProfile"
            v-model="academicProfile"
            :user="userModel"
            :readonly="readonly"
          />
        </custom-tab-item>
        <custom-tab-item
          v-if="
            ((!isSpecialists || isDirectPaymentInstitution) &&
              canAddLocation(applicationIdsEnum.financePortal)) ||
              canAssignedProposalArea
          "
          title="Portal de Proveedor"
          :lazy="false"
        >
          <template
            v-if="
              (!isSpecialists || isDirectPaymentInstitution) &&
                canAddLocation(applicationIdsEnum.financePortal)
            "
          >
            <position-assignment
              class="mt-2"
              v-if="canSetPosition && readonly"
              ref="positionAssigment"
              :profile="userPosition"
              :readonly="readonly"
              :user-profile="userModel"
            />
            <mipe-profile
              class="mb-2"
              ref="providerProfile"
              v-model="providerProfile"
              :user="userModel"
              :readonly="readonly"
              is-finance-user
              :is-login-user-provider="isLoginUserProvider"
            />
          </template>
          <template v-if="canAssignedProposalArea">
            <chips-selection
              selected-items-title="Área(s) y Seleccionada(s):"
              v-model="userModel.proposalAreas"
              @addingItem="addingArea"
              :chips-config="areaConfig"
              :disabled="readonly"
            >
              <template #selectionInputs>
                <validation-observer ref="areaForm" disabled>
                  <div class="form-group">
                    <validation-provider rules="required" v-slot="{ errors }">
                      <custom-drop-down
                        v-model="selectedProposalArea"
                        label="Área:"
                        :options="availableAreas"
                        fieldname="name"
                        fieldkey="id"
                        placeholder="Seleccione"
                        :error="errors[0]"
                        custom-error-msg="La área es requerida."
                        @input="onAppOrAreaSelected"
                      />
                    </validation-provider>
                    <custom-drop-down
                      v-model="selectedProposalUnit"
                      label="Unidad Oficina:"
                      :options="availableUnits"
                      fieldname="name"
                      fieldkey="id"
                      placeholder="Seleccione"
                      @input="onAppSelected"
                      :disabled="!selectedProposalArea"
                    />
                  </div>
                </validation-observer>
              </template>
            </chips-selection>
          </template>
        </custom-tab-item>
        <custom-tab-item
          v-if="canAddLocation(applicationIdsEnum.remProPortal)"
          title="Portal de Remedio Provisional"
          :lazy="false"
        >
          <position-assignment
            class="mt-2"
            v-if="canSetPosition && readonly"
            ref="positionAssigment"
            :profile="userPosition"
            :readonly="readonly"
            :user-profile="userModel"
          />
          <mipe-profile
            ref="remproProfile"
            v-model="providerProfile"
            :user="userModel"
            :readonly="readonly"
            :is-rp-user="true"
          />
        </custom-tab-item>
        <custom-tab-item
          v-if="showDETicketsProfile"
          title="DE Tickets"
          :lazy="false"
        >
          <de-tickets-profile ref="deTicketsProfile" :user="userModel" />
        </custom-tab-item>
        <custom-tab-item
          title="Portal de Asistencia Tecnológica"
          :lazy="false"
          v-if="showCaatProfile"
        >
          <c-a-a-t-profile
            ref="caatProfile"
            v-model="caatProfile"
            :user="userModel"
            :readonly="readonly"
          />
        </custom-tab-item>
      </custom-tab>

      <div v-if="showParentExternal" class="parent-collapse__container">
        <custom-collapse id="parentCollapse">
          <validation-observer
            ref="parentParentesco"
            v-slot="{ valid, validated }"
          >
            <custom-collapse-item
              title="Información del Parentesco"
              id="parentInfoItem"
              parent-id="parentCollapse"
              class="mt-3"
              style="background-color: #f7f7f7;"
              selected
              :error="validated && !valid"
              custom-error-msg="Favor completar informacion requerida"
            >
              <div class="row">
                <div class="col-md-2">
                  <validation-provider rules="required" v-slot="{ errors }">
                    <custom-drop-down
                      id="ddlrelationship"
                      v-model="userModel.parentPortalInfo.relationship"
                      fieldname="name"
                      fieldkey="id"
                      :options="relationships"
                      placeholder="Seleccione"
                      label="Parentesco:"
                      :error="errors[0]"
                      :disabled="readonly"
                      custom-error-msg="El parentesco es requerido."
                    />
                  </validation-provider>
                </div>
              </div>
            </custom-collapse-item>
          </validation-observer>

          <validation-observer ref="parentAdress" v-slot="{ valid, validated }">
            <custom-collapse-item
              title="Información de Direccion(es)"
              id="parentAddrestem"
              parent-id="parentCollapse"
              class="mt-3"
              style="background-color: #f7f7f7;"
              selected
              :error="validated && !valid"
              custom-error-msg="Favor completar informacion requerida"
            >
              <div class="row">
                <div class="col-md-12">
                  <span>Dirección Física</span>
                </div>

                <div class="col-md-6 col-sm-12 mt-3 pt-0">
                  <validation-provider rules="required" v-slot="{ errors }">
                    <custom-input
                      id="txtAdressLine1"
                      label="Dirección (Línea 1):"
                      v-model="userModel.parentPortalInfo.physicalAddressLine1"
                      :error="errors[0]"
                      :disabled="readonly"
                      custom-error-msg="La dirección (línea 1) es requerida."
                    />
                  </validation-provider>
                </div>
                <div class="col-md-6 col-sm-12 mt-3 pt-0">
                  <custom-input
                    id="txtAdressLine2"
                    label="Dirección (Línea 2):"
                    :disabled="readonly"
                    v-model="userModel.parentPortalInfo.physicalAddressLine2"
                  />
                </div>
                <div class="col-md-6 col-sm-12 mt-3">
                  <validation-provider rules="required" v-slot="{ errors }">
                    <custom-drop-down
                      v-model="
                        userModel.parentPortalInfo.physicalAddressMunicipality
                      "
                      id="physicalAddressMunicipality"
                      fieldkey="id"
                      fieldname="name"
                      :options="municipality"
                      :clearable="true"
                      label="Municipio:"
                      placeholder="Seleccione"
                      :error="errors[0]"
                      :disabled="readonly"
                      custom-error-msg="El municipio es requerido."
                    />
                  </validation-provider>
                </div>
                <div class="col-md-6 col-sm-12 mt-3 pt-0">
                  <validation-provider rules="required" v-slot="{ errors }">
                    <custom-input
                      v-model="
                        userModel.parentPortalInfo.physicalAddressPostalCode
                      "
                      id="physicalAddressPostalCode"
                      label="Código Postal:"
                      :error="errors[0]"
                      :disabled="readonly"
                      custom-error-msg="El código postal es requerido."
                    />
                  </validation-provider>
                </div>

                <div class="col-md-12 mt-3">
                  <span>Dirección Postal</span>
                </div>

                <div class="col-md-6 col-sm-12 mt-3 pt-0">
                  <validation-provider rules="required" v-slot="{ errors }">
                    <custom-input
                      id="txtPostalAdressLine1"
                      label="Dirección (Línea 1):"
                      :disabled="readonly || postalAddressSameAsPhysical"
                      v-model="userModel.parentPortalInfo.postalAddressLine1"
                      :error="errors[0]"
                      custom-error-msg="La dirección (línea 1) es requerida."
                    />
                  </validation-provider>
                </div>
                <div class="col-md-6 col-sm-12 mt-3 pt-0">
                  <custom-input
                    id="txtPostalAdressLine2"
                    label="Dirección (Línea 2):"
                    :disabled="readonly || postalAddressSameAsPhysical"
                    v-model="userModel.parentPortalInfo.postalAddressLine2"
                  />
                </div>
                <div class="col-md-6 col-sm-12 mt-3">
                  <validation-provider rules="required" v-slot="{ errors }">
                    <custom-drop-down
                      v-model="
                        userModel.parentPortalInfo.postalAddressMunicipality
                      "
                      id="postalAddressMunicipality"
                      fieldkey="id"
                      fieldname="name"
                      :options="municipality"
                      :clearable="true"
                      placeholder="Seleccione"
                      label="Municipio:"
                      :disabled="readonly || postalAddressSameAsPhysical"
                      :error="errors[0]"
                      custom-error-msg="El municipio es requerido."
                    />
                  </validation-provider>
                </div>
                <div class="col-md-6 col-sm-12 mt-3 pt-0">
                  <validation-provider rules="required" v-slot="{ errors }">
                    <custom-input
                      id="postalAddressPostalCode"
                      label="Código Postal:"
                      :disabled="readonly || postalAddressSameAsPhysical"
                      v-model="
                        userModel.parentPortalInfo.postalAddressPostalCode
                      "
                      :error="errors[0]"
                      custom-error-msg="El código postal es requerido."
                    />
                  </validation-provider>
                </div>
                <div class="col-md-2 col-sm-12 mt-4">
                  <div
                    class="custom-control custom-checkbox checkbox-top-spacing"
                  >
                    <input
                      type="checkbox"
                      name="postalAddressSameAsPhysical"
                      id="postalAddressSameAsPhysical"
                      class="custom-control-input"
                      :disabled="readonly"
                      :checked="postalAddressSameAsPhysical"
                      v-model="postalAddressSameAsPhysical"
                    />

                    <label
                      for="postalAddressSameAsPhysical"
                      class="custom-control-label font regular"
                    >
                      <span><em class="ic-check" /></span>
                      La misma dirección que la física
                    </label>
                  </div>
                </div>
              </div>
            </custom-collapse-item>
          </validation-observer>

          <validation-observer
            ref="parentContact"
            v-slot="{ valid, validated }"
          >
            <custom-collapse-item
              title="Información de Contacto del Padre, Madre o Encargado"
              id="parentContactInfoItem"
              parent-id="parentCollapse"
              class="mt-3"
              style="background-color: #f7f7f7;"
              selected
              :error="validated && !valid"
              custom-error-msg="Favor completar informacion requerida"
            >
              <div class="row">
                <div class="col-md-4 col-sm-12 mt-3 pt-0">
                  <validation-provider
                    v-slot="{ errors }"
                    :custom-messages="{
                      required:
                        'El nombre del padre, madre o encargado es requerido.',
                    }"
                    name="validationCustomtPhone1"
                    rules="completePhone|required"
                  >
                    <custom-input
                      id="parentPortalName"
                      label="Nombre de Padre, Madre o Encargado:"
                      v-model="userModel.parentPortalInfo.name"
                      :error="errors[0]"
                      :disabled="readonly"
                    />
                  </validation-provider>
                </div>
                <div class="col-md-4 col-sm-12 mt-3 pt-0">
                  <validation-provider
                    v-slot="{ errors }"
                    :custom-messages="{
                      required:
                        'El apellido del padre, madre o encargado es requerido.',
                    }"
                    name="validationCustomtPhone1"
                    rules="completePhone|required"
                  >
                    <custom-input
                      id="parentPortalSurname"
                      label="Apellido de Padre, Madre o Encargado:"
                      v-model="userModel.parentPortalInfo.surname"
                      :error="errors[0]"
                      :disabled="readonly"
                    />
                  </validation-provider>
                </div>
                <div class="col-md-4 col-sm-12 mt-3 pt-0">
                  <validation-provider
                    :rules="{ required: true, email: true }"
                    v-slot="{ errors }"
                    name=" "
                  >
                    <custom-input
                      id="parentPortalEmail"
                      label="Correo Electrónico del Contacto Portal de Padres:"
                      v-model="userModel.parentPortalInfo.email"
                      :error="errors[0]"
                      :disabled="readonly"
                      :custom-messages="{
                        required: 'El correo electrónico es requerido.',
                        email: 'El correo electrónico no es válido.',
                      }"
                    />
                  </validation-provider>
                </div>
                <div class="col-md-4 col-sm-12 mt-3 pt-0">
                  <validation-provider
                    v-slot="{ errors }"
                    :custom-messages="{
                      completePhone: 'El número de contacto no es válido.',
                      required: 'El teléfono contacto es requerido.',
                    }"
                    name="validationCustomtPhone1"
                    rules="completePhone|required"
                  >
                    <custom-input
                      id="parentPortalPhone"
                      label="Teléfono de Contacto:"
                      v-model="userModel.parentPortalInfo.phone"
                      :error="errors[0]"
                      :disabled="readonly"
                      mask-type="Phone"
                      placeholder="(___) ___-____"
                    />
                  </validation-provider>
                </div>
                <div class="col-md-4 col-sm-12 mt-3">
                  <validation-provider rules="required" v-slot="{ errors }">
                    <custom-drop-down
                      v-model="userModel.parentPortalInfo.provider"
                      id="parentPortalProvider"
                      fieldkey="id"
                      fieldname="name"
                      :options="providers"
                      :clearable="false"
                      label="Proveedor:"
                      placeholder="Seleccione"
                      :disabled="readonly"
                      :error="errors[0]"
                      custom-error-msg="El proveedor de teléfono es requerido."
                    />
                  </validation-provider>
                </div>
              </div>
            </custom-collapse-item>
          </validation-observer>
        </custom-collapse>
      </div>
    </div>
  </div>
</template>

<script>
import SectionTitle from 'SectionTitle';
import CustomDropDown from 'custom-drop-down';
import CustomTab from 'custom-tabs/src/CustomTab';
import CustomTabItem from 'custom-tabs/src/CustomTabItem';
import ChipsSelection from '@/components/common/ChipsSelection';
import MipeProfile from '@/components/common/user/appProfiles/MipeProfile';
import CAATProfile from '@/components/common/user/appProfiles/CAATProfile';
import DeTicketsProfile from '@/components/common/user/appProfiles/DETicketsProfile.vue';
import ParentProfile from '@/components/common/user/appProfiles/ParentProfile.vue';
import PositionAssignment from '@/components/common/user/PositionAssignment.vue';
import SipaeeProfile from '@/components/common/user/appProfiles/SipaeeProfile.vue';

import applicationIds from '@/utils/constants/applicationIds';
import rolesIds from '@/utils/constants/rolesIds';
import accessLevel from '@/utils/constants/accessLevel';
import { getRolesByApplicattionId } from '@/modules/roles/api';
import { getApplicationsList } from '@/modules/applications/api';
import interfacesServices from '@/modules/interfaces/api';
import eduespService from '@/services/eduesp';
import { extend } from 'vee-validate';
import { getSysParamById } from '../../../modules/common/api';
import alertMixin from 'utils/mixins/alert.mixin.js';

import { CustomCollapse, CustomCollapseItem } from '@/components/common';
import { getParentCatalogues } from '@/modules/users/api';
import Roles from '@/utils/constants/rolesIds';
import CustomInput from 'CustomInput';
import deticketsService from '@/services/detickets';

extend('rolesRequired', {
  validate(selectedRoles, args) {
    return {
      required: true,
      valid: selectedRoles?.length >= args.length,
    };
  },
  computesRequired: true,
  params: ['length'],
  message: 'Favor seleccione el rol.',
});

const ROLES_CHIP_UTILS = {
  CONFIG: [
    { labelText: 'Sistema:', valueProp: 'applicationDisplayName' },
    { labelText: 'Rol:', valueProp: 'roleName' },
  ],
  VALIDATION: () => ({
    on: ['selectionChanged'],
  }),
};

const AREAS_AND_UNIT_CHIP_UTILS = {
  CONFIG: [
    { labelText: 'Area:', valueProp: 'name' },
    { labelText: 'Unidad Oficina:', valueProp: 'unitName' },
  ],
};

export default {
  name: 'AppRoleAssignment',
  components: {
    SectionTitle,
    CustomDropDown,
    ChipsSelection,
    CustomTab,
    CustomTabItem,
    MipeProfile,
    ParentProfile,
    DeTicketsProfile,
    PositionAssignment,
    CAATProfile,
    CustomCollapse,
    CustomCollapseItem,
    CustomInput,
    SipaeeProfile,
  },
  mixins: [alertMixin],
  model: { prop: 'user' },
  props: {
    data: {
      type: Object,
      required: true,
    },
    user: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: () => false,
    },
    selectableApps: {
      type: Array,
      default: () => Object.values(applicationIds),
    },
    isLoginUserProvider: {
      type: Boolean,
      default: () => false,
    },
  },
  data: () => ({
    adminRoles: [],
    areaConfig: AREAS_AND_UNIT_CHIP_UTILS.CONFIG,
    chipConfig: ROLES_CHIP_UTILS.CONFIG,
    providerValidationMode: ROLES_CHIP_UTILS.VALIDATION,
    selectedProposalArea: null,
    selectedProposalUnit: null,
    userPosition: {},
    academicProfile: {
      userLocations: [],
    },
    caatProfile: {
      caatLocations: [],
    },
    providerProfile: {
      userLocations: [],
    },
    deTicketsProfile: {
      deptId: null,
    },
    model: {
      app: null,
      role: null,
    },
    options: {
      apps: [],
      roles: [],
      areas: [],
      units: [],
    },
    students: [],
    postalOriginalValue: {
      postalAddressLine1: null,
      postalAddressLine2: null,
      postalAddressMunicipality: null,
      postalAddressPostalCode: null,
    },
    postalAddressSameAsPhysical: false,
    providers: [],
    municipality: [],
    relationships: [],
  }),
  computed: {
    parentAddessesAreEmpty() {
      let areEmpty = true;
      if (!this.showParentExternal) return areEmpty;

      areEmpty =
        areEmpty && !this.userModel.parentPortalInfo.physicalAddressLine1;
      areEmpty =
        areEmpty && !this.userModel.parentPortalInfo.physicalAddressLine2;
      areEmpty =
        areEmpty && !this.userModel.parentPortalInfo.physicalAddressPostalCode;
      areEmpty =
        areEmpty &&
        !this.userModel.parentPortalInfo.physicalAddressMunicipality;

      areEmpty =
        areEmpty && !this.userModel.parentPortalInfo.postalAddressLine1;
      areEmpty =
        areEmpty && !this.userModel.parentPortalInfo.postalAddressLine2;
      areEmpty =
        areEmpty && !this.userModel.parentPortalInfo.postalAddressPostalCode;
      areEmpty =
        areEmpty && !this.userModel.parentPortalInfo.postalAddressMunicipality;

      return areEmpty;
    },
    showParentExternal() {
      const hasRequiredRole =
        this.userModel?.roles?.some(
          (x) =>
            x.applicationId.toUpperCase() == applicationIds.portalPadres &&
            x.roleId.toUpperCase() == Roles.PadreExterno
        ) ?? false;

      return hasRequiredRole;
    },
    isSpecialists() {
      const roles = this.userModel?.roles || [];
      const isSpecialist = roles.some((x) => {
        const role = x.roleId?.toLowerCase();
        return (
          role === rolesIds.Especialista.toLowerCase() ||
          role === rolesIds.EspecialistaRP.toLowerCase()
        );
      });
      return isSpecialist;
    },
    isDirectPaymentInstitution() {
      const roles = this.userModel?.roles || [];
      const isDirectPaymentInstitution = roles.some((x) => {
        const role = x.roleId?.toLowerCase();
        return (
          role === rolesIds.ProviderPagoDirecto.toLowerCase() ||
          role === rolesIds.AsistenteProviderPagoDirecto.toLowerCase()
        );
      });
      return isDirectPaymentInstitution;
    },
    applicationIdsEnum() {
      return applicationIds;
    },
    userModel: {
      get() {
        return this.user || {};
      },
      set(val) {
        this.$emit('input', val);
      },
    },
    availableApps() {
      return this.options.apps.filter(
        (a) =>
          this.selectableApps.includes(a.id.toUpperCase()) &&
          (this.userModel.roles || []).every((r) => r.applicationId !== a.id)
      );
    },
    availableAreas() {
      return this.options.areas.filter((a) =>
        (this.userModel.proposalAreas || []).every((r) => r.id !== a.id)
      );
    },
    availableUnits() {
      if (!this.selectedProposalArea) return [];

      // Unidades ya seleccionadas para ESTA área
      const alreadyForArea = (this.userModel.proposalAreas || [])
        .filter((r) => r.id === this.selectedProposalArea.id)
        .map((r) => r.unitId);

      // options.units ya viene filtrado por el área actual
      return this.options.units.filter((u) => !alreadyForArea.includes(u.id));
    },
    showDETicketsProfile() {
      return this.userModel.roles?.some(
        (x) =>
          x.applicationId.toUpperCase() == this.applicationIdsEnum.detickets
      );
    },
    showCaatProfile() {
      const caatRoles = [rolesIds.CaatRequirente, rolesIds.CAATTechnician];
      return (
        this.userModel?.roles &&
        this.userModel?.roles?.some((x) =>
          caatRoles.includes(x.roleId.toUpperCase())
        )
      );
    },
    roleByApp() {
      return (application) =>
        this.userModel.roles?.find(
          (x) => x.applicationId.toUpperCase() == application
        );
    },
    canSetProfileInfo() {
      return (
        this.userModel.roles?.length &&
        this.userModel.roles.some(
          (r) =>
            ![
              //applicationIds.detickets,
              applicationIds.securityPortal,
              applicationIds.reportsPortal,
            ].includes(r.applicationId.toUpperCase())
        )
      );
    },
    hasProfileInformation() {
      return (
        (this.userPosition?.eduespPositionName &&
          this.userPosition?.teachPositionNo &&
          this.userPosition?.staffId) ||
        this.academicProfile?.userLocations?.length ||
        this.providerProfile?.userLocations?.length ||
        this.caatProfile?.caatLocations?.length ||
        this.data?.students?.length
      );
    },
    canSetPosition() {
      return this.userModel.roles?.some((r) =>
        [
          applicationIds.mipe,
          applicationIds.financePortal,
          applicationIds.remProPortal,
        ].includes(r.applicationId.toUpperCase())
      );
    },
    canAddLocation() {
      return (application) => {
        const role = this.roleByApp(application);
        if (!role) return false;

        const correctAccessLevel =
          role.applicationId?.toUpperCase() == applicationIds.financePortal
            ? role.accessLevel == accessLevel.company ||
              role.accessLevel == accessLevel.location ||
              role.accessLevel == accessLevel.companySpecialist ||
              role.accessLevel == accessLevel.centerCompany
            : role.applicationId?.toUpperCase() == applicationIds.remProPortal
            ? role.accessLevel == accessLevel.company
            : ![
                accessLevel.none,
                accessLevel.company,
                accessLevel.centerCompany,
              ].includes(role.accessLevel);

        return correctAccessLevel;
      };
    },
    hasMipeProfile() {
      return this.$refs.mipeProfile;
    },
    hasProviderProfile() {
      return this.$refs.providerProfile || this.$refs.remproProfile;
    },
    hasCaatProfile() {
      return this.$refs.caatProfile;
    },
    getLockedItems() {
      let lockedItems = this.userHasPermissions(
        'security.user.candeleteadminrole'
      )
        ? []
        : this.adminRoles;

      //para especialistas o especialistas RP
      if (!this.userHasPermissions('security.user.candeletespecialistrole')) {
        let specialistRoles = this.userModel?.roles
          ?.filter((x) => x.isLocked === true)
          .map((u) => u.roleId.toUpperCase());
        Array.prototype.push.apply(lockedItems, specialistRoles);
      }
      return lockedItems;
    },
    canChangeSpecialistRol() {
      return this.userHasPermissions('security.user.canchangespecialistrole');
    },
    // JSF: if the user's been edited already have one specialist role (Especialist o EspecialistaRP)
    hasRoleSpecialists() {
      const specialists = [rolesIds.Especialista, rolesIds.EspecialistaRP];
      return this.userModel.roles?.some((r) =>
        specialists.includes(r.roleId.toUpperCase())
      );
    },
    canAssignedProposalArea() {
      const hasFinancePortal = this.userModel.roles?.some((r) =>
        [applicationIds.financePortal].includes(r.applicationId.toUpperCase())
      );

      const rfpRoles = [rolesIds.AdministradorRFP, rolesIds.ApoyoRFP];
      return (
        (hasFinancePortal &&
          this.userHasPermissions('provider.proposal.editarea')) ||
        this.userModel.roles?.some((role) =>
          rfpRoles.includes(role.roleId.toUpperCase())
        )
      );
    },
  },
  watch: {
    postalAddressSameAsPhysical(newVal) {
      if (newVal) {
        this.postalOriginalValue = {
          postalAddressLine1: this.userModel.parentPortalInfo
            .postalAddressLine1,
          postalAddressLine2: this.userModel.parentPortalInfo
            .postalAddressLine2,
          postalAddressMunicipality: this.userModel.parentPortalInfo
            .postalAddressMunicipality,
          postalAddressPostalCode: this.userModel.parentPortalInfo
            .postalAddressPostalCode,
        };

        this.userModel.parentPortalInfo.postalAddressLine1 = this.userModel.parentPortalInfo.physicalAddressLine1;
        this.userModel.parentPortalInfo.postalAddressLine2 = this.userModel.parentPortalInfo.physicalAddressLine2;
        this.userModel.parentPortalInfo.postalAddressMunicipality = this.userModel.parentPortalInfo.physicalAddressMunicipality;
        this.userModel.parentPortalInfo.postalAddressPostalCode = this.userModel.parentPortalInfo.physicalAddressPostalCode;
      } else {
        this.userModel.parentPortalInfo.postalAddressLine1 = this.postalOriginalValue.postalAddressLine1;
        this.userModel.parentPortalInfo.postalAddressLine2 = this.postalOriginalValue.postalAddressLine2;
        this.userModel.parentPortalInfo.postalAddressMunicipality = this.postalOriginalValue.postalAddressMunicipality;
        this.userModel.parentPortalInfo.postalAddressPostalCode = this.postalOriginalValue.postalAddressPostalCode;
      }
    },
    'userModel.parentPortalInfo.physicalAddressLine1'(newVal) {
      if (this.postalAddressSameAsPhysical) {
        this.userModel.parentPortalInfo.postalAddressLine1 = newVal;
      }
    },
    'userModel.parentPortalInfo.physicalAddressLine2'(newVal) {
      if (this.postalAddressSameAsPhysical) {
        this.userModel.parentPortalInfo.postalAddressLine2 = newVal;
      }
    },
    'userModel.parentPortalInfo.physicalAddressMunicipality'(newVal) {
      if (this.postalAddressSameAsPhysical) {
        this.userModel.parentPortalInfo.postalAddressMunicipality = newVal;
      }
    },
    'userModel.parentPortalInfo.physicalAddressPostalCode'(newVal) {
      if (this.postalAddressSameAsPhysical) {
        this.userModel.parentPortalInfo.postalAddressPostalCode = newVal;
      }
    },
    user: {
      deep: true,
      immediate: true,
      async handler(newVal) {
        if (newVal?.roles?.length) {
          await this.getProfileData();
        }

        if (newVal?.profile) {
          const profile = { ...newVal.profile };
          const userLocations = this.divideUserLocations(profile);

          this.userPosition = {
            eduespPositionCode: profile.eduespPositionCode,
            eduespPositionName: profile.eduespPositionName,
            teachPositionNo: profile.teachPositionNo,
            staffId: profile.staffId,
          };

          this.academicProfile = { userLocations: userLocations.academic };
          this.providerProfile = { userLocations: userLocations.provider };
          this.caatProfile = { caatLocations: userLocations.caat };
        }

        this.$emit('changed');
      },
    },
  },
  async mounted() {
    await this.getApps();
    await this.getRoles();
    await this.getProposalAreas();
    await this.fetchCatalogues();

    if (this.$refs.parentProfile && this.data.students?.length > 0) {
      this.$refs.parentProfile.model.selectedStudents = this.data.students;
    }

    this.setSameAddressCheckbox();
  },
  methods: {
    async onAppOrAreaSelected(value) {
      await this.onAppSelected(value);
      if (this.selectedProposalArea) {
        await this.onAreaChanged(this.selectedProposalArea);
      }
    },
    async onAreaChanged(area) {
      this.selectedProposalArea = area || null;

      this.selectedProposalUnit = null;
      this.options.units = [];

      if (!area) return;

      try {
        const { data } = await interfacesServices.getServiceOfficeTypesByArea(
          area.id
        );
        this.options.units = data ?? [];
      } catch {
        this.options.units = [];
      }
    },
    async setStudentAddressIfExist(sieId) {
      if (!this.parentAddessesAreEmpty) return;

      const { data } = await eduespService.getStudentAddress(sieId);
      this.postalAddressSameAsPhysical = false;

      this.userModel.parentPortalInfo.physicalAddressLine1 =
        data.physicalAddress?.address1;
      this.userModel.parentPortalInfo.physicalAddressLine2 =
        data.physicalAddress?.address2;
      this.userModel.parentPortalInfo.physicalAddressPostalCode =
        data.physicalAddress?.zipCode;
      this.userModel.parentPortalInfo.physicalAddressMunicipality =
        data.physicalAddress?.municipality;

      this.userModel.parentPortalInfo.postalAddressLine1 =
        data.postalAddress?.address1;
      this.userModel.parentPortalInfo.postalAddressLine2 =
        data.postalAddress?.address2;
      this.userModel.parentPortalInfo.postalAddressPostalCode =
        data.postalAddress?.zipCode;
      this.userModel.parentPortalInfo.postalAddressMunicipality =
        data.postalAddress?.municipality;
    },
    setSameAddressCheckbox() {
      setTimeout(() => {
        if (!this.showParentExternal) return;

        let itsSame = true;
        itsSame =
          itsSame &&
          this.userModel.parentPortalInfo.postalAddressLine1 ==
            this.userModel.parentPortalInfo.physicalAddressLine1;
        itsSame =
          itsSame &&
          this.userModel.parentPortalInfo.postalAddressLine2 ==
            this.userModel.parentPortalInfo.physicalAddressLine2;
        itsSame =
          itsSame &&
          this.userModel.parentPortalInfo.postalAddressMunicipality?.id ==
            this.userModel.parentPortalInfo.physicalAddressMunicipality?.id;
        itsSame =
          itsSame &&
          this.userModel.parentPortalInfo.postalAddressPostalCode ==
            this.userModel.parentPortalInfo.physicalAddressPostalCode;

        this.postalAddressSameAsPhysical = itsSame;
      }, 500);
    },
    onChangePostalAddressSameAsPhysical() {
      if (this.postalAddressSameAsPhysical) {
        this.postalAddressPostalCode_stg = this.userModel.parentPortalInfo.postalAddressPostalCode;
        this.postalAddressLine1_stg = this.userModel.parentPortalInfo.postalAddressLine1;
        this.postalAddressLine2_stg = this.userModel.parentPortalInfo.postalAddressLine2;
        this.postalAddressMunicipality_stg = this.userModel.parentPortalInfo.postalAddressMunicipality;

        this.userModel.parentPortalInfo.postalAddressPostalCode = this.userModel.parentPortalInfo.physicalAddressPostalCode;
        this.userModel.parentPortalInfo.postalAddressLine1 = this.userModel.parentPortalInfo.physicalAddressLine1;
        this.userModel.parentPortalInfo.postalAddressLine2 = this.userModel.parentPortalInfo.physicalAddressLine2;
        this.userModel.parentPortalInfo.postalAddressMunicipality = this.userModel.parentPortalInfo.physicalAddressMunicipality;
      } else {
        this.userModel.parentPortalInfo.postalAddressPostalCode = this.postalAddressPostalCode_stg;
        this.userModel.parentPortalInfo.postalAddressLine1 = this.postalAddressLine1_stg;
        this.userModel.parentPortalInfo.postalAddressLine2 = this.postalAddressLine2_stg;
        this.userModel.parentPortalInfo.postalAddressMunicipality = this.postalAddressMunicipality_stg;
      }
    },
    async fetchCatalogues() {
      try {
        this.municipality = await eduespService.getMunicipalities();
        const res = await getParentCatalogues();
        this.providers = res.data.providers;
        this.relationships = res.data.relationships;
      } catch (err) {
        this.ShowToast('Alerta', 'Error cargando los datos', 'error');
        this.response = err;
      }
    },
    async getGeneralAccountInfo(info) {
      this.$emit('getGeneralAccountInfo', info);
    },
    async getRoles() {
      await getSysParamById(100).then((r) => {
        this.adminRoles = r.data.staticValue.split('|');
      });
    },
    async getApps() {
      this.options.apps = await getApplicationsList();
    },
    async getProposalAreas() {
      this.options.areas = (await interfacesServices.getProposalAreas()).data;
    },
    async onAppSelected() {
      this.model.role = null;
      this.options.roles = [];
      // JSF: US93700 bring specialist's roles if the user already has an specialist's role (either specialist or specialistRP).
      const skipSpecialistRole = !this.hasRoleSpecialists;
      if (this.model.app) {
        const { data } = await getRolesByApplicattionId(
          this.model.app.id,
          // JSF: US90026 hide specialist's roles when creating an user or editing an user which has not specialist role assinged.
          skipSpecialistRole
        );

        const hasPermission = this.userHasPermissions(
          'security.user.canselectadminrol'
        );

        const ignoreRoles = [rolesIds.ProveedorRP];

        //Se ha habilitado la opción de seleccionar roles de proveedorRP segun criteterios de PO, US102098
        const currentApplication = this.model.app.id.toUpperCase();
        const applicationExcept = applicationIds.remProPortal.toUpperCase();

        if (applicationExcept === currentApplication) {
          ignoreRoles.pop();
        }

        ignoreRoles.push(rolesIds.AdminIL);
        ignoreRoles.push(rolesIds.Administrador);
        ignoreRoles.push(rolesIds.AdministradorRP);
        ignoreRoles.push(rolesIds.AdminProvider);
        ignoreRoles.push(rolesIds.AdminSAEE);
        ignoreRoles.push(rolesIds.UMESRAdmin);

        data.forEach((item) => {
          if (
            (ignoreRoles.includes(item.roleId.toUpperCase()) ||
              this.adminRoles.includes(item.roleId.toUpperCase())) &&
            !hasPermission
          ) {
            return;
          }

          this.options.roles.push(item);
          this.$emit('changed');
        });
      }
    },
    async getProfileData() {
      if (
        this.userModel.userId == null ||
        (this.userPosition && Object.keys(this.userPosition).length)
      )
        return;

      const role =
        this.roleByApp(applicationIds.mipe) ||
        this.roleByApp(applicationIds.remProPortal) ||
        this.roleByApp(applicationIds.financePortal) ||
        this.roleByApp(applicationIds.caat);

      if (role) {
        role.accessLevel = this.userModel.roles?.find(
          (x) => x.accessLevel == accessLevel.centerCompany
        )
          ? accessLevel.centerCompany
          : role.accessLevel;

        const userProfile =
          (await eduespService.getUserProfile(
            this.userModel.userId,
            role.roleId,
            role.accessLevel
          )) || {};

        this.userPosition = {
          eduespPositionCode: userProfile.eduespPositionCode,
          eduespPositionName: userProfile.eduespPositionName,
          teachPositionNo: userProfile.teachPositionNo,
          staffId: userProfile.staffId,
          userId: userProfile.userId,
        };

        const userLocations = this.divideUserLocations(
          userProfile,
          role.accessLevel
        );

        this.academicProfile = {
          userId: userProfile.userId,
          userLocations: userLocations.academic,
        };
        this.providerProfile = {
          userId: userProfile.userId,
          userLocations: userLocations.provider,
        };
        this.caatProfile = {
          userId: userProfile.userId,
          caatLocations: userLocations.caat,
        };
      }
    },
    //ECG:event handler manejado especialmente para esperar por su resultado
    //ECG:No hay que devolver el item nuevo, puedes añadir a la coleccion directamente si deseas
    async addingRole() {
      const valid = await this.$refs.roleForm.validate();
      if (!valid) return;

      const applications = [
        applicationIds.financePortal,
        applicationIds.remProPortal,
      ];

      const roles = this.userModel.roles?.filter(
        (x) =>
          applications.includes(x.applicationId.toUpperCase()) &&
          x.applicationId.toUpperCase() != this.model.app.id.toUpperCase()
      );

      if (
        (this.model.app.id.toUpperCase() ===
          applicationIds.financePortal.toUpperCase() &&
          this.model.role.roleId.toUpperCase() ===
            rolesIds.Proveedor.toUpperCase()) ||
        (this.model.app.id.toUpperCase() ===
          applicationIds.remProPortal.toUpperCase() &&
          this.model.role.roleId.toUpperCase() ===
            rolesIds.ProveedorRP.toUpperCase())
      ) {
        if (
          roles != null &&
          roles.some(
            (x) =>
              (x.applicationId.toUpperCase() ===
                applicationIds.financePortal.toUpperCase() &&
                x.roleId.toUpperCase() === rolesIds.Proveedor.toUpperCase()) ||
              (x.applicationId.toUpperCase() ===
                applicationIds.remProPortal.toUpperCase() &&
                x.roleId.toUpperCase() === rolesIds.ProveedorRP.toUpperCase())
          )
        ) {
          this.Toast(
            'Alerta',
            'No es posible asignar el rol de proveedor a los portales de proveedores y remedio provisional.',
            'error'
          );
          return;
        }
      }

      const selection = {
        applicationId: this.model.app.id,
        applicationDisplayName: this.model.app.name,
        roleName: this.model.role.roleName,
        roleId: this.model.role.roleId,
        accessLevel: this.model.role.accessLevel,
      };

      this.resetRoleForm();
      return selection;
    },
    removingRole(removedRole) {
      switch (removedRole.applicationId.toUpperCase()) {
        case applicationIds.mipe:
          this.academicProfile.userLocations = [];
          break;
        case applicationIds.financePortal:
          this.providerProfile.userLocations = [];
          this.userModel.proposalAreas = [];
          break;
        case applicationIds.caat:
          this.caatProfile.caatLocations = [];
          break;
      }
    },
    resetRoleForm() {
      this.model = {
        app: null,
        role: null,
      };
      this.options.roles = [];
      this.$refs.roleForm.reset();
    },
    async addingArea() {
      const valid = await this.$refs.areaForm.validate();
      if (!valid) return;

      const selection = {
        id: this.selectedProposalArea?.id,
        name: this.selectedProposalArea?.name,
        unitId: this.selectedProposalUnit?.id,
        unitName: this.selectedProposalUnit?.name,
      };

      this.selectedProposalArea = null;
      this.selectedProposalUnit = null;
      this.$refs.areaForm.reset();
      return selection;
    },
    divideUserLocations({ userLocations }, userAccessLevel) {
      const res = { provider: [], academic: [], caat: [] };

      if (userAccessLevel && userAccessLevel == accessLevel.centerCompany) {
        res.provider = userLocations;
        return res;
      }

      (userLocations || []).forEach((location) => {
        if (location.companyId != null && location.caatId == null) {
          res.provider.push(location);
        } else if (location.caatId == null) {
          res.academic.push(location);
        } else {
          res.caat.push(location);
        }
      });
      return res;
    },
    validateProfileTab(appId) {
      switch (appId.toUpperCase()) {
        case applicationIds.mipe:
          return this.$refs.mipeProfile?.validate();
        case applicationIds.financePortal:
          return this.$refs.providerProfile?.validate();
        case applicationIds.remProPortal:
          return this.$refs.providerProfile?.validate();
        case applicationIds.portalPadres:
          return this.$refs.parentProfile?.validate();
        case applicationIds.detickets:
          return this.$refs.deTicketsProfile?.validate();
        default:
          return Promise.resolve(true);
      }
    },
    async validate() {
      const rolesPassed = await this.$refs.roleSelection.validate();
      let validations = [];

      for (const p of this.userModel.roles) {
        const profilePassed = await this.validateProfileTab(p.applicationId);
        validations.push(profilePassed);
      }

      let parentIsValid = true;
      if (this.showParentExternal) {
        parentIsValid =
          parentIsValid && (await this.$refs.parentParentesco.validate());
        parentIsValid =
          parentIsValid && (await this.$refs.parentAdress.validate());
        parentIsValid =
          parentIsValid && (await this.$refs.parentContact.validate());
      }

      return (
        rolesPassed && !validations.some((v) => v === false) && parentIsValid
      );
    },
    saveProfiles() {
      if (!this.validate()) return;

      const profilePromises = [];
      for (const profile of this.userModel.roles) {
        switch (profile.applicationId.toUpperCase()) {
          case applicationIds.portalPadres:
            profilePromises.push(this.$refs.parentProfile.save); //fn reference
            break;
          case applicationIds.detickets:
            if (this.showDETicketsProfile) {
              profilePromises.push(this.$refs.deTicketsProfile.save);
            }
            break;
        }
      }

      const deTicketsProfile = this.userModel.profiles.find(
        (x) =>
          x.applicationId.toUpperCase() == this.applicationIdsEnum.detickets
      );
      if (!this.showDETicketsProfile && deTicketsProfile) {
        const profile = {
          profileId: deTicketsProfile.profileId,
          firstName: deTicketsProfile.firstName,
          lastName1: deTicketsProfile.lastName1,
          lastName2: deTicketsProfile.lastName2,
          email: deTicketsProfile.email,
          deptIds: [],
          applicationId: applicationIds.detickets,
          SSN: deTicketsProfile.SSN,
          isActiveDirectoryUser: deTicketsProfile.IsActiveDirectoryUser,
          isDeleted: false,
          createsCasesOnlyForSelf: false,
        };

        profilePromises.push(deticketsService.updateProfile(profile));
      }

      profilePromises.push(this.saveMipeProfile); //always call this fn to create eduesp_user for any application
      profilePromises.push(this.getProfileData); //get profile information after create profile
      const hasParentPortal = this.userModel.roles.some(
        (x) => x.applicationId.toUpperCase() == applicationIds.portalPadres
      );

      if (!hasParentPortal && !!this.userPosition?.userId) {
        profilePromises.push(this.clearParentProfile); //call this fn to clear parent profile
      }

      return profilePromises;
    },
    getMipeProfile() {
      const positionModel = this.$refs?.positionAssigment?.getModel() || {}; //set default object to any application <> mipe and provider
      return {
        ...positionModel,
        applicationId: applicationIds.mipe,
        academicLocations: this.hasMipeProfile
          ? this.academicProfile.userLocations
          : [],
        providerLocations: this.hasProviderProfile
          ? this.providerProfile.userLocations
          : [],
        caatLocations: this.hasCaatProfile
          ? this.caatProfile.caatLocations
          : [],
      };
    },
    saveMipeProfile() {
      const profile = { ...this.getMipeProfile() };
      let data = {
        ...profile,
        userId: this.userPosition.userId,
        userLocations: [
          ...profile.academicLocations,
          ...profile.providerLocations,
          ...profile.caatLocations,
        ],
      };
      delete data.academicLocations;
      delete data.providerLocations;
      delete data.caatLocations;

      data.userId = this.userModel.userId;

      if (data.userId && this.userModel.operation === 'U') {
        data = {
          ...data,
          activeFlag: this.userModel.activeFlag ? '1' : '0',
          firstName: this.userModel.firstName,
          middleName: this.userModel.middleInitial,
          lastMaternalName: this.userModel.maternalLastName,
          lastPaternalName: this.userModel.paternalLastName,
        };
        return eduespService.updateUserProfile(data);
      }

      return eduespService.createUserProfile(data);
    },
    clearParentProfile() {
      return eduespService.saveParentProfileInformation(
        this.userPosition.userId,
        []
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.parent-collapse__container {
  padding: 15px 30px;
}

.collapse-item__title {
  font-weight: bold;
}

@import '@/sass/config/_colors.scss';

.role-assignment {
  ::v-deep .profile-tabs {
    border-bottom: unset;
    margin-top: -0.25rem;

    .custom-tab-container {
      --tab-active-color: #{map-get($colors, 'green')} !important;
    }

    .tab-content {
      margin-top: 0 !important;
      padding: 1rem;
    }

    @media only screen and (max-width: 768px) {
      .tab-content {
        padding: 1rem 0;
      }
    }
  }
}
</style>
